import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { db } from "../../../firebase/config";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import { useAuth } from "../../../context/AuthContext";
import { useTheme } from "../../../context/ThemeContext";
import Header from "../../../components/Header";
import { registrarAccion } from "../../../utils/trazabilidad";

const ESTADOS = {
  borrador: { label: 'Borrador', color: '#AEB6C4', bg: 'rgba(174,182,196,0.15)' },
  terminado: { label: 'Terminado', color: '#00B8FF', bg: 'rgba(0,184,255,0.15)' },
  impreso_firmado: { label: 'Impreso y firmado', color: '#00D084', bg: 'rgba(0,208,132,0.15)' },
};

export default function DetalleConsentimiento() {
  const { id } = useParams();
  const { currentUser, userRole, clinicaId, clinicaData } = useAuth();
  const { theme } = useTheme();
  const [consentimiento, setConsentimiento] = useState(null);
  const [editando, setEditando] = useState(false);
  const [textoEdit, setTextoEdit] = useState('');
  const [procedimientoEdit, setProcedimientoEdit] = useState('');

  const canEdit = ['gestion_general', 'recepcion', 'medico'].includes(userRole);

  const btnPrimaryStyle = { background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentDark})`, border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: '#FFFFFF', fontWeight: '600', fontFamily: "'Montserrat', sans-serif" };
  const btnSecondaryStyle = { background: 'transparent', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: theme.secondary, fontFamily: "'Montserrat', sans-serif" };
  const inputStyle = { width: '100%', padding: '10px 14px', fontSize: '13px', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', outline: 'none', color: theme.primary, background: theme.bgInput, boxSizing: 'border-box', fontFamily: "'Montserrat', sans-serif" };

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'consentimientos', id), snap => {
      if (snap.exists()) {
        const data = { id: snap.id, ...snap.data() };
        setConsentimiento(data);
        setTextoEdit(data.texto || '');
        setProcedimientoEdit(data.procedimiento || '');
      }
    });
    return unsub;
  }, [id]);

  async function guardarEdicion() {
    await updateDoc(doc(db, 'consentimientos', id), { texto: textoEdit, procedimiento: procedimientoEdit });
    await registrarAccion(currentUser?.email, userRole, 'Consentimientos', 'Editar consentimiento', consentimiento.pacienteNombre, clinicaId);
    setEditando(false);
  }

  async function finalizarEImprimir() {
    await updateDoc(doc(db, 'consentimientos', id), { estado: 'terminado', terminadoPor: currentUser?.email, fechaTerminado: new Date() });
    await registrarAccion(currentUser?.email, userRole, 'Consentimientos', 'Finalizar consentimiento', consentimiento.pacienteNombre, clinicaId);
    setTimeout(() => imprimirConsentimiento(), 300);
  }

  async function marcarImpreso() {
    await updateDoc(doc(db, 'consentimientos', id), { estado: 'impreso_firmado', fechaImpreso: new Date() });
    await registrarAccion(currentUser?.email, userRole, 'Consentimientos', 'Marcar impreso y firmado', consentimiento.pacienteNombre, clinicaId);
  }

  function imprimirConsentimiento() {
    const ventana = window.open('', '_blank');
    ventana.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Consentimiento — ${consentimiento.pacienteNombre}</title>
        <style>
          body { font-family: Arial, sans-serif; max-width: 800px; margin: 40px auto; padding: 0 40px; color: #1A2740; }
          .header { text-align: center; margin-bottom: 32px; border-bottom: 2px solid #0099DD; padding-bottom: 16px; }
          .logo { height: 48px; width: auto; margin-bottom: 8px; }
          .titulo { font-size: 20px; font-weight: 700; margin: 16px 0 4px; color: #1A2740; }
          .subtitulo { font-size: 13px; color: #5A6A85; }
          .datos { background: #F0F4FA; padding: 16px; border-radius: 8px; margin: 20px 0; display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
          .dato label { font-size: 10px; color: #5A6A85; text-transform: uppercase; font-weight: 600; }
          .dato p { font-size: 14px; margin: 2px 0; }
          .texto { line-height: 1.7; font-size: 14px; margin: 24px 0; text-align: justify; }
          .firma { margin-top: 48px; display: grid; grid-template-columns: 1fr 1fr; gap: 32px; }
          .firma-bloque { border-top: 1px solid #C0CCE0; padding-top: 8px; text-align: center; font-size: 12px; color: #5A6A85; }
          @media print { body { margin: 20px; } }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="titulo">CONSENTIMIENTO INFORMADO</div>
          <div class="subtitulo">${consentimiento.procedimiento}</div>
        </div>
        <div class="datos">
          <div class="dato"><label>Paciente</label><p>${consentimiento.pacienteNombre}</p></div>
          <div class="dato"><label>DNI</label><p>${consentimiento.pacienteDni || '—'}</p></div>
          <div class="dato"><label>Fecha de nacimiento</label><p>${consentimiento.pacienteFechaNac || '—'}</p></div>
          <div class="dato"><label>Médico</label><p>${consentimiento.medico === 'medico1' ? (clinicaData?.medicos?.medico1 || 'Médico 1') : (clinicaData?.medicos?.medico2 || 'Médico 2')}</p></div>
        </div>
        <div class="texto">${consentimiento.texto?.replace(/\n/g, '<br>') || ''}</div>
        <div class="firma">
          <div class="firma-bloque">Firma del paciente<br><br>_____________________</div>
          <div class="firma-bloque">Firma del profesional<br><br>_____________________</div>
        </div>
        <div style="margin-top:32px; font-size:11px; color:#5A6A85; text-align:center;">
          Documento generado el ${new Date().toLocaleDateString('es-AR')}
        </div>
      </body>
      </html>
    `);
    ventana.document.close();
    ventana.print();
  }

  if (!consentimiento) return <div style={{ minHeight: '100vh', background: theme.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.secondary, fontFamily: "'Montserrat', sans-serif" }}>Cargando...</div>;

  const estado = ESTADOS[consentimiento.estado] || { label: consentimiento.estado, color: theme.secondary, bg: theme.bgCard };

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: "'Montserrat', sans-serif" }}>
      <Header titulo={consentimiento.pacienteNombre} backLabel="Consentimientos" backRuta="/consentimientos" />

      <div style={{ padding: '12px 24px 0', display: 'flex', justifyContent: 'flex-end', gap: '8px', background: theme.bg, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: '12px', padding: '4px 12px', borderRadius: '99px', fontWeight: '500', background: estado.bg, color: estado.color }}>{estado.label}</span>
        {canEdit && consentimiento.estado === 'borrador' && !editando && (
          <button onClick={() => setEditando(true)} style={btnSecondaryStyle}>Editar</button>
        )}
        {editando && (
          <>
            <button onClick={guardarEdicion} style={btnPrimaryStyle}>Guardar cambios</button>
            <button onClick={() => setEditando(false)} style={btnSecondaryStyle}>Cancelar</button>
          </>
        )}
        {canEdit && consentimiento.estado === 'borrador' && !editando && (
          <button onClick={finalizarEImprimir} style={btnPrimaryStyle}>Finalizar e imprimir</button>
        )}
        {consentimiento.estado === 'terminado' && (
          <>
            <button onClick={imprimirConsentimiento} style={btnSecondaryStyle}>Reimprimir</button>
            <button onClick={marcarImpreso} style={btnPrimaryStyle}>Marcar impreso y firmado</button>
          </>
        )}
        {consentimiento.estado === 'impreso_firmado' && (
          <button onClick={imprimirConsentimiento} style={btnSecondaryStyle}>Reimprimir</button>
        )}
      </div>

      <div style={{ padding: '24px', background: theme.bg, display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <div style={{ background: theme.bgCard, borderRadius: '12px', border: `0.5px solid ${theme.border}`, padding: '20px' }}>
          <div style={{ fontSize: '11px', color: theme.accent, fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '16px' }}>Datos</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
            {[
              ['PACIENTE', consentimiento.pacienteNombre],
              ['DNI', consentimiento.pacienteDni || '—'],
              ['FECHA DE NACIMIENTO', consentimiento.pacienteFechaNac || '—'],
              ['MÉDICO', consentimiento.medico === 'medico1' ? (clinicaData?.medicos?.medico1 || 'Médico 1') : (clinicaData?.medicos?.medico2 || 'Médico 2')],
              ['CREADO POR', consentimiento.creadoPor],
              ['FECHA', consentimiento.creadoEn?.toDate ? consentimiento.creadoEn.toDate().toLocaleDateString('es-AR') : '—'],
            ].map(([label, valor]) => (
              <div key={label}>
                <div style={{ fontSize: '11px', color: theme.secondary, marginBottom: '4px', fontWeight: '500', letterSpacing: '1px' }}>{label}</div>
                <div style={{ fontSize: '13px', color: theme.primary, fontWeight: '500' }}>{valor}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ background: theme.bgCard, borderRadius: '12px', border: `0.5px solid ${theme.border}`, padding: '20px' }}>
          <div style={{ fontSize: '11px', color: theme.accent, fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '16px' }}>Procedimiento</div>
          {editando
            ? <input value={procedimientoEdit} onChange={e => setProcedimientoEdit(e.target.value)} style={inputStyle} />
            : <div style={{ fontSize: '14px', color: theme.primary, fontWeight: '500' }}>{consentimiento.procedimiento}</div>
          }
        </div>

        <div style={{ background: theme.bgCard, borderRadius: '12px', border: `0.5px solid ${theme.border}`, padding: '20px' }}>
          <div style={{ fontSize: '11px', color: theme.accent, fontWeight: '700', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '16px' }}>Texto del consentimiento</div>
          {editando
            ? <textarea value={textoEdit} onChange={e => setTextoEdit(e.target.value)} rows={10} style={{ ...inputStyle, resize: 'vertical', lineHeight: '1.6' }} />
            : <div style={{ fontSize: '14px', color: theme.primary, lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>{consentimiento.texto}</div>
          }
        </div>
      </div>
    </div>
  );
}